'use strict';

var IdGenerator = require('./IdGenerator');
var log = require('../../../../log/Log').channel('CallsDevice');

// The CallsDevice mixin keeps track of all the call interfaces in an array.
// Each item of the array is a copy of the equipment's config information for
// the corresponding entry telephony section of the config, plus the following:
//
// * configId: The key in the telephony config (e.g., 'a0' for config.telephony.a0)
// * callStatus: Status information about the active or most recent call. This
//     is the information sent to EquipmentService as part of the 'callStatus'
//     controlStatus message. It contains the following sub-properties:
//     * callId: the Firebase id for the call.
//     * callState: 'ringing', 'connected', 'disconnecting', or 'disconnected'
//     * callType: one of Kaylee.callTypes
//     * contactName: the caller ID information for the call
//     * dialString: the string used for dialing the call
//     * direction: 'incoming' or 'outgoing'
//     * displayDialString: the dial string to display in the user interface
//     * end: the timestamp for the end of the call
//     * extraDialString: additional digits dialed after the call is connected
//     * missed: Boolean for whether or not the call was missed
//     * receiveGain: number indicating the current receive gain value in dB, between -10 and 10
//     * start: the timestamp for the start of the call
// * callType: The type of the call: This property is not set by this mixin;
//     the driver is responsible for setting it during the validation phase.
// * name: A unique name given to each call interface. This property is not set
//     by this mixin; the driver is responsible for setting it during the
//     validation phase. Typically it the name of the control point used for
//     the call interface in the device's API.

function CallsDevice() {
    this.callInterfaces = [];
    this.callStateChangedHandlers = [];
}

CallsDevice.prototype.extraCallProperties = [
    [ 'canEndMeeting' ],
    [ 'canInvite', ],
    [ 'layouts' ],
    [ 'receiveGain' ],
    [ 'sendingContent' ],
    [ 'sendingVideo' ],
];

// Answer an incoming call. Send a callStatus message if the callState changes.

CallsDevice.prototype.answerCall = function(ci) {
    var changed = false;
    var prevCallState = this.getCallState(ci);

    if (!ci.hasOwnProperty('callStatus') ||
        ci.callStatus.callState === 'disconnected') {
        log.error(this + ': answering disconnected call');
        ci.callStatus = {
            callId: this.getNewCallId(),
            callType: ci.callType,
            direction: 'incoming',
            start: Date.now(),
            callState: 'connected',
            canEndMeeting: this.defaultCallCanEndMeeting,
        };

        this.applyCallProperties(ci);
        changed = true;
    } else if (ci.callStatus.callState === 'ringing' &&
        ci.callStatus.direction === 'incoming') {
        ci.callStatus.callState = 'connected';
        changed = true;
    } else if (ci.callStatus.callState !== 'connected') {
        log.error(this + ': answering with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
        ci.callStatus.callState = 'connected';
        changed = true;
    }

    if (changed) {
        ci.callStatus.canDial = this.callCanDial(ci);

        log.debug(this + ': answerCall: ci = ' + JSON.stringify(ci));

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));

            this.callStateChangedHandlers.forEach(function(handler) {
                handler.call(this, ci, prevCallState);
            }, this);
        }

    }
};

// Copy additional call properties from the call interface object to the
// callStatus property. This is used internally when a new call appears on a
// call interface.

CallsDevice.prototype.applyCallProperties = function(ci) {
    for (var i = 0; i < this.extraCallProperties.length; i++) {
        var path = this.extraCallProperties[i];
        var ciValue = this.getNode(ci, path);

        if (typeof ciValue === 'object') {
            ciValue = JSON.parse(JSON.stringify(ciValue));
        }

        if (ciValue !== null && ci.hasOwnProperty('callStatus')) {
            this.setNode(ci.callStatus, path, ciValue);
        }
    }

    // Receive gain was handled differently in the past; this is for
    // backwards compatibility with drivers that use the old technique.

    if (ci.hasOwnProperty('receiveGainValue')) {
        ci.callStatus.receiveGain = ci.receiveGainValue;
    }
};

// Function for determining when a call can dial extra DTMF digits. Drivers
// may provide their own behavior by implementing onCallCanDial. 

CallsDevice.prototype.callCanDial = function(ci) {
    if (typeof this.onCallCanDial === 'function') {
        return this.onCallCanDial(ci);
    }

    return ci.hasOwnProperty('callStatus') &&
        ci.callStatus.callState === 'connected';
};

// Helper function to compute the maxCalls properties in caps based on the
// callType properties that the driver added during the validation state. This
// can also be called after the config has been updated, but before validation.
// In that case, all calls are assumed to be of the type specified by the
// defaultCallType parameter (or 'voip' if none is specified).

CallsDevice.prototype.computeCapsMaxCalls = function(caps, defaultCallType) {
    if (typeof defaultCallType === 'undefined') defaultCallType = 'voip';

    caps.maxCalls.pstn = 0;
    caps.maxCalls.teams = 0;
    caps.maxCalls.video = 0;
    caps.maxCalls.voip = 0;
    caps.maxCalls.zoom = 0;

    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        var callType =
            ci.hasOwnProperty('callType') ? ci.callType : defaultCallType;
        caps.maxCalls[callType]++;
    }

    return caps;
};

// Connect a call. This should be used in the following circumstances.
//
// 1. To transition a 'ringing' call to 'connected' when the appropriate status
// message is recieved from the device.
//
// 2. When a status message from the device indicates that a call is connected.
// This can happen if Inara starts up when a device is already in a call or if
// a third party controller places or answers a call.
//
// 3. Instead of placeCall for call interfaces (such as most PSTN interfaces)
// that do not support a status message for outgoing calls to transition from
// 'ringing' to 'connected'.
//
// This also sends a callStatus message if the call state, dial string, and/or
// contact name are changed.

CallsDevice.prototype.connectCall = function(ci, callId, dialString, contactName,
    displayDialString) {
    var changed = false;
    var prevCallState = this.getCallState(ci);

    if (!ci.hasOwnProperty('callStatus') ||
        ci.callStatus.callState === 'disconnected') {
        ci.callStatus = {
            callId: typeof callId !== 'undefined' && callId !== null ? callId : this.getNewCallId(),
            callType: ci.callType,
            direction: 'outgoing', // guess outgoing
            start: Date.now(),
            callState: 'connected',
            canEndMeeting: this.defaultCallCanEndMeeting,
        };

        this.applyCallProperties(ci);
        changed = true;
    } else if (ci.callStatus.callState === 'ringing') {
        ci.callStatus.callState = 'connected';
        changed = true;
    } else if (ci.callStatus.callState !== 'connected') {
        log.error(this + ': connecting call with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
        ci.callStatus.callState = 'connected';
        changed = true;
    }

    var callStateChanged = changed;

    changed = this.updateCallerId(ci, dialString, contactName,
        displayDialString, false) || changed;

    if (changed) {
        ci.callStatus.canDial = this.callCanDial(ci);

        log.debug(this + ': connectCall: ci = ' + JSON.stringify(ci));

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }

    if (callStateChanged && this.fsm.current === 'controlling') {
        this.callStateChangedHandlers.forEach(function(handler) {
            handler.call(this, ci, prevCallState);
        }, this);
    }
};

// Dial additional digits while a call is connected. Sends a callStatus message.

CallsDevice.prototype.dialCall = function(ci, dialString) {
    if (!ci.hasOwnProperty('callStatus')) {
        log.error(this + ': can\'t dial call with no call status');
        return;
    } else if (ci.callStatus.callState !== 'connected') {
        log.error(this + ': dialing call with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
    }

    if (ci.callStatus.hasOwnProperty('extraDialString')) {
        ci.callStatus.extraDialString += dialString;
    } else {
        ci.callStatus.extraDialString = dialString;
    }

    log.debug(this + ': dialCall: ci = ' + JSON.stringify(ci));

    this.broadcastStatus('callStatus',
        JSON.parse(JSON.stringify(ci.callStatus)));
};

// Disconnect a call. This should be used in the following circumstances.
//
// 1. To transition a 'disconnecting' call to 'disconnected' when the
// appropriate status message is received from the device.
//
// 2. Instead of endCall for call interfaces (such as most PSTN interfaces)
// that do not support a status message to transition calls from
// 'disconnecting' to 'disconnected'.
//
// This also sends a callStatus message if the call state changes.

CallsDevice.prototype.disconnectCall = function(ci) {
    var prevCallState;

    if (!ci.hasOwnProperty('callStatus')) {
        return;
    } else if (ci.callStatus.callState === 'disconnected') {
        return;
    } else {
        prevCallState = this.getCallState(ci);
        if (ci.callStatus.callState === 'ringing') ci.callStatus.missed = true;
        ci.callStatus.callState = 'disconnected';
        if (!ci.callStatus.hasOwnProperty('end')) {
            ci.callStatus.end = Date.now();
        }
    }

    ci.callStatus.canDial = this.callCanDial(ci);

    log.debug(this + ': disconnectCall: ci = ' + JSON.stringify(ci));

    if (this.fsm.current === 'controlling') {
        this.broadcastStatus('callStatus',
            JSON.parse(JSON.stringify(ci.callStatus)));

        this.callStateChangedHandlers.forEach(function(handler) {
            handler.call(this, ci, prevCallState);
        }, this);
    }
};

// End a call. This should be used both for rejecting an incoming call and
// hanging up an outgoing call. It sends a callStatus message if the call state
// changes.

CallsDevice.prototype.endCall = function(ci) {
    var prevCallState = this.getCallState(ci);

    if (!ci.hasOwnProperty('callStatus')) {
        log.error(this + ': can\'t end call with no callStatus');
        return;
    } else if (ci.callStatus.callState === 'connected' ||
        ci.callStatus.callState === 'ringing') {
        if (ci.callStatus.callState === 'ringing') ci.callStatus.missed = true;
        ci.callStatus.callState = 'disconnecting';
        ci.callStatus.end = Date.now();
    } else if (ci.callStatus.callState === 'disconnecting' ||
        ci.callStatus.callState === 'disconnected') {
        return;
    } else {
        log.error(this + ': ending call with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
        ci.callStatus.callState = 'disconnecting';
        ci.callStatus.end = Date.now();
    }

    ci.callStatus.canDial = this.callCanDial(ci);

    log.debug(this + ': endCall: ci = ' + JSON.stringify(ci));

    if (this.fsm.current === 'controlling') {
        this.broadcastStatus('callStatus',
            JSON.parse(JSON.stringify(ci.callStatus)));

        if (prevCallState !== 'disconnecting') {
            this.callStateChangedHandlers.forEach(function(handler) {
                handler.call(this, ci, prevCallState);
            }, this);
        }
    }
};

// Function for drivers to look up an available (i.e., not in a call) call
// interface of the specified callType.

CallsDevice.prototype.getAvailableCallInterface = function(callType) {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];

        if (ci.callType === callType &&
            (!ci.hasOwnProperty('callStatus') ||
             ci.callStatus.callState === 'disconnected')) return ci;
    }

    return null;
};

// Function for drivers to look up a call interface by its callId.

CallsDevice.prototype.getCallInterfaceByCallId = function(callId) {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        if (ci.hasOwnProperty('callStatus') &&
            ci.callStatus.callId === callId) {
            return ci;
        }
    }

    return null;
};

// Function for drivers to look up a call interface by its configId.

CallsDevice.prototype.getCallInterfaceByConfigId = function(configId) {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        if (ci.configId === configId) return ci;
    }

    return null;
};

// Function for drivers to look up a call interface by its driver-provided
// name.

CallsDevice.prototype.getCallInterfaceByName = function(name) {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        if (ci.hasOwnProperty('name') && ci.name === name) {
            return ci;
        }
    }

    return null;
};

// Conveniece function for drivers to get the callState for a call interface.

CallsDevice.prototype.getCallState = function(ci) {
    if (!ci.hasOwnProperty('callStatus') ||
        !ci.callStatus.hasOwnProperty('callState')) return 'disconnected';
    return ci.callStatus.callState;
};

// Internal function for generating a new call ID.

CallsDevice.prototype.getNewCallId = function() {
    return IdGenerator.generateId();
};

// Implementation of getNumActiveCalls, which is required for all drivers with
// call interfaces.

CallsDevice.prototype.getNumActiveCalls = function(callType) {
    var numCalls = 0;

    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        if (ci.callType === callType &&
            ci.hasOwnProperty('callStatus') &&
            ci.callStatus.callState !== 'disconnected') {
            numCalls++;
        }
    }

    return numCalls;
};

// Return true if the device is in a call. A specific type or types of call may
// be specified as well.

CallsDevice.prototype.isInCall = function(types) {
    if (typeof types === 'undefined') {
        // TODO: this needs to be kept in sync with KayleeCalls.callTypes for
        // now. This should be fixed eventually.
        types = [ 'googleMeet', 'pstn', 'teams', 'video', 'voip', 'zoom' ];
    } else if (typeof types === 'string') {
        types = [ types ];
    }

    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];

        if (types.includes(ci.callType) &&
            this.getNode(ci, [ 'callStatus', 'callState' ]) === 'connected') {
            return true;
        }
    }

    return false;
};

// Register a handler to be called after the call state changes.

CallsDevice.prototype.onCallStateChanged = function(handler) {
    this.callStateChangedHandlers.push(handler);
};

// Implementation of onDestroy. If drivers need to do their own work in
// onDestroy, they should explicitly call this method in their implementation.

CallsDevice.prototype.onDestroy = function() {
    // Hang up any active calls before destroying the device.

    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];

        if (ci.hasOwnProperty('callStatus') &&
            ci.callStatus.callState !== 'disconnected') {
            this.disconnectCall(ci);
        }
    }

    this.callStateChangedHandlers = [];
};

// Implementation of onUpdateConfig. Drivers can forego implementing this if they
// have no other work to do. If they do have work to do, they should explicitly
// call this method in their implementation.

CallsDevice.prototype.onUpdateConfig = function() {
    this.callInterfaces.length = 0;

    if (this.config.hasOwnProperty('telephony')) {
        var telephonyKeys = Object.keys(this.config.telephony).sort();

        for (var i = 0; i < telephonyKeys.length; i++) {
            var key = telephonyKeys[i];
            var ci;

            if (typeof this.config.telephony[key] === 'string') {
                ci = { name: this.config.telephony[key] };
            } else {
                ci = JSON.parse(JSON.stringify(this.config.telephony[key]));
            }

            ci.configId = key;

            if (ci.hasOwnProperty('receiveGain')) {
                ci.receiveGainValue = 0;
            }

            this.callInterfaces.push(ci);
        }
    }

    log.debug(this + ': onUpdateConfig: ' + this.callInterfaces.length +
        ' call interfaces');
};

// Start an outgoing call. Send a callStatus message if the callState, dial
// string, and/or contact name changes.

CallsDevice.prototype.placeCall = function(ci, callId, dialString, contactName,
    displayDialString) {
    var changed = false;
    var prevCallState = this.getCallState(ci);

    if (!ci.hasOwnProperty('callStatus') ||
        ci.callStatus.callState === 'disconnected') {
        ci.callStatus = {
            callId: typeof callId !== 'undefined' && callId !== null ? callId : this.getNewCallId(),
            callType: ci.callType,
            direction: 'outgoing',
            start: Date.now(),
            callState: 'ringing',
            canEndMeeting: this.defaultCallCanEndMeeting,
        };

        this.applyCallProperties(ci);
        changed = true;
    } else if (ci.callStatus.callState !== 'ringing') {
        log.error(this + ': placing call with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
        ci.callStatus.callState = 'ringing';
        changed = true;
    }

    var callStateChanged = changed;

    changed = this.updateCallerId(ci, dialString, contactName,
        displayDialString, false) || changed;

    if (changed) {
        ci.callStatus.canDial = this.callCanDial(ci);

        log.debug(this + ': placeCall: ci = ' + JSON.stringify(ci));

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }

    if (callStateChanged && this.fsm.current === 'controlling') {
        this.callStateChangedHandlers.forEach(function(handler) {
            handler.call(this, ci, prevCallState);
        }, this);
    }
};

// Remove a call interface from the callInterfaces array.

CallsDevice.prototype.removeCallInterfaceByName = function(name) {
    var foundIndex = null;

    for (var i = 0; i < this.callInterfaces.length; i++) {
        if (this.callInterfaces[i].name === name) {
            foundIndex = i;
            break;
        }
    }

    if (foundIndex === null) return false;

    this.callInterfaces.splice(foundIndex, 1);
    return true;
};

// Reset the call interfaces. Should be called by the clients when reconnecting.
// Typically, this is done in onControl.

CallsDevice.prototype.resetCallInterfaces = function() {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        delete this.callInterfaces[i].callStatus;
    }
};

// Send call status messages in response to a callStatus query.

CallsDevice.prototype.sendCallStatusMessages = function() {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];
        if (ci.hasOwnProperty('callStatus')) {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }
};

CallsDevice.prototype.sendVideo = function(ci, send) {
    if (!ci.hasOwnProperty('callStatus')) {
        log.error(this + ': can\'t sendVideo on call with no call status');
        return;
    }

    if (ci.callStatus.callState !== 'connected') {
        log.error(this + ': sendVideo on call with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
    }

    ci.callStatus.sendingVideo = send;

    log.debug(this + ': sendVideo: ci = ' + JSON.stringify(ci));

    this.broadcastStatus('callStatus',
        JSON.parse(JSON.stringify(ci.callStatus)));
};

// Mark an incoming call as ringing. Sends a callStatus message if the call
// state changes.

CallsDevice.prototype.setIncomingCall = function(ci, dialString, contactName,
    displayDialString) {
    var changed = false;
    var prevCallState = this.getCallState(ci);

    if (!ci.hasOwnProperty('callStatus') ||
        ci.callStatus.callState === 'disconnected') {
        ci.callStatus = {
            callId: this.getNewCallId(),
            callType: ci.callType,
            direction: 'incoming',
            start: Date.now(),
            callState: 'ringing',
            canEndMeeting: this.defaultCallCanEndMeeting,
        };

        this.applyCallProperties(ci);
        changed = true;
    } else if (ci.callStatus.callState !== 'ringing') {
        log.error(this + ': setting ringing state on call with invalid callStatus: ' +
            JSON.stringify(ci.callStatus, null, 4));
        ci.callStatus.callState = 'ringing';
        changed = true;
    }

    var callStateChanged = changed;

    changed = this.updateCallerId(ci, dialString, contactName,
        displayDialString, false) || changed;

    if (changed) {
        ci.callStatus.canDial = this.callCanDial(ci);

        log.debug(this + ': setIncomingCall: ci = ' + JSON.stringify(ci));

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }

    if (callStateChanged && this.fsm.current === 'controlling') {
        this.callStateChangedHandlers.forEach(function(handler) {
            handler.call(this, ci, prevCallState);
        }, this);
    }
};

// Mark an outgoing call as ringing. Sends a callStatus message if the call
// state changes. This should be used when a status message is received from
// the device indicating that there is an outgoing call ringing due to the call
// being placed by a third party controller.

CallsDevice.prototype.setOutgoingCall = function(ci, dialString, contactName,
    displayDialString, callId) {
    var changed = false;
    var prevCallState = this.getCallState(ci);

    if (!ci.hasOwnProperty('callStatus') ||
        ci.callStatus.callState === 'disconnected') {
        ci.callStatus = {
            callId: typeof callId !== 'undefined' && callId !== null ? callId : this.getNewCallId(),
            callType: ci.callType,
            direction: 'outgoing',
            start: Date.now(),
            callState: 'ringing',
            canEndMeeting: this.defaultCallCanEndMeeting,
        };

        this.applyCallProperties(ci);
        changed = true;
    } else if (ci.callStatus.callState !== 'ringing') {
        log.error(this + ': setting ringing state on call with invalid callStatus:' +
            JSON.stringify(ci.callStatus, null, 4));
        ci.callStatus.callState = 'ringing';
        changed = true;
    }

    var callStateChanged = changed;

    changed = this.updateCallerId(ci, dialString, contactName,
        displayDialString, false) || changed;

    if (changed) {
        ci.callStatus.canDial = this.callCanDial(ci);

        log.debug(this + ': setOutgoingCall: ci = ' + JSON.stringify(ci));

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }

    if (callStateChanged && this.fsm.current === 'controlling') {
        this.callStateChangedHandlers.forEach(function(handler) {
            handler.call(this, ci, prevCallState);
        }, this);
    }
};

// Update the caller ID for the call. Send status message if the caller ID
// changes (sending can be suppressed by specifying broadcastStatus as
// false).

CallsDevice.prototype.updateCallerId = function(ci, dialString, contactName,
    displayDialString, broadcastStatus) {
    if (typeof broadcastStatus === 'undefined') {
        broadcastStatus = true;
    }

    var changed = false;

    if (!ci.hasOwnProperty('callStatus')) return false;

    if (typeof dialString !== 'undefined') {
        if (dialString === null || dialString === '') {
            if (ci.callStatus.hasOwnProperty('dialString')) {
                delete ci.callStatus.dialString;
                changed = true;
            }
        } else if (!ci.callStatus.hasOwnProperty('dialString') ||
            ci.callStatus.dialString !== dialString) {
            ci.callStatus.dialString = dialString;
            changed = true;
        }
    }

    if (typeof contactName !== 'undefined') {
        if (contactName === null || contactName === '') {
            if (ci.callStatus.hasOwnProperty('contactName')) {
                delete ci.callStatus.contactName;
                changed = true;
            }
        } else if (!ci.callStatus.hasOwnProperty('contactName') ||
            ci.callStatus.contactName !== contactName) {
            ci.callStatus.contactName = contactName;
            changed = true;
        }
    }

    if (typeof displayDialString !== 'undefined') {
        if (displayDialString === null || displayDialString === '') {
            if (ci.callStatus.hasOwnProperty('displayDialString')) {
                delete ci.callStatus.displayDialString;
                changed = true;
            }
        } else if (!ci.callStatus.hasOwnProperty('displayDialString') ||
            ci.callStatus.displayDialString !== displayDialString) {
            ci.callStatus.displayDialString = displayDialString;
            changed = true;
        }
    }

    if (changed) {
        log.debug(this + ': updateCallerId: ci = ' + JSON.stringify(ci));

        if (broadcastStatus && this.fsm.current === 'controlling') {
            this.broadcastStatus('callStatus',
                JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }

    return changed;
};

// Update a property on the call interface. If we're in a call, send a status
// message if the value changes.

CallsDevice.prototype.updateCallProperty = function(ci, path, value) {
    if (typeof path === 'string') path = [ path ];
    if (typeof value === 'object') value = JSON.parse(JSON.stringify(value));

    this.setNode(ci, path, value);

    if (!ci.hasOwnProperty('callStatus')) return;

    if (!this.objEquals(this.getNode(ci.callStatus, path), value)) {
        this.setNode(ci.callStatus, path, value);

        log.debug(this + ': updateCallProperty: ci = ' + JSON.stringify(ci));

        if (this.fsm.current === 'controlling' &&
            this.callStatus.callState !== 'disconnected') {
            this.broadcastStatus('callStatus', JSON.parse(JSON.stringify(ci.callStatus)));
        }
    }
};

// Update the receive gain for the call. Send status message if it changes.
// Note: This function is deprecated; updateCallProperty should be used instead.

CallsDevice.prototype.updateReceiveGain = function(ci, receiveGainValue) {
    if (typeof receiveGainValue === 'string') {
        receiveGainValue = parseFloat(receiveGainValue);
    }

    if (receiveGainValue < -10) receiveGainValue = -10;
    if (receiveGainValue > 10) receiveGainValue = 10;

    var changed = receiveGainValue !== ci.receiveGainValue;

    ci.receiveGainValue = receiveGainValue;

    if (changed) {
        log.debug(this + ': updateReceiveGain: ci = ' + JSON.stringify(ci));
    }

    if (this.fsm.current === 'controlling' &&
        ci.hasOwnProperty('callStatus') &&
        (!ci.callStatus.hasOwnProperty('receiveGain') ||
         ci.callStatus.receiveGain !== receiveGainValue)) {
        ci.callStatus.receiveGain = receiveGainValue;
        this.broadcastStatus('callStatus',
            JSON.parse(JSON.stringify(ci.callStatus)));
    }
};

module.exports = CallsDevice;
